/**
 * htmlResponseGuard — "a JSON endpoint that answers with HTML must fail LOUDLY".
 *
 * AAS-I18N-002 W8 / user ruling 23 (2026-08-10). See
 * `errors/HtmlResponseError.js` for the incident this generalises: a preference
 * write that composed to the web origin, got `index.html` back with a **200**,
 * and silently discarded the user's change at six call sites in one app.
 *
 * This module is deliberately split from `createApiClient` so the two clients in
 * the ecosystem that are NOT axios can share the exact same detector rather than
 * re-implement it:
 *   - `apps/purchase-web/src/core/api/apiClient.js` (hand-rolled `fetch` adapter)
 *   - `apps/port-web/src/…/platformApi.js` (blob export; cannot adopt axios)
 *
 * ## Detection rules, and why each one is drawn where it is
 *
 * 1. **The caller must have asked for JSON.** axios `responseType` defaults to
 *    `'json'`; anything else (`'text'`, `'blob'`, `'document'`, `'arraybuffer'`,
 *    `'stream'`) is a caller who may legitimately want markup — the blog-serve
 *    and export surfaces do. Those are skipped, not flagged.
 * 2. **An explicit opt-out exists**: `{ allowHtmlResponse: true }` on the request
 *    config. A guard with no escape hatch gets deleted the first time it is
 *    inconvenient; one with a named, greppable opt-out survives.
 * 3. **`content-type` is authoritative when present.** `text/html` ⇒ fire;
 *    `application/json` ⇒ never fire, even if the body looks like markup (a JSON
 *    string field legitimately can).
 * 4. **Body sniffing is the FALLBACK only** — used when no content-type came
 *    back at all. It matches a leading `<!doctype html` or `<html`, which is
 *    what every SPA history fallback emits, after trimming leading whitespace
 *    and an optional BOM.
 *
 * The asymmetry in 3/4 is load-bearing: sniffing first would flag a legitimate
 * JSON payload whose first field happens to be an HTML fragment (the CMS page
 * builder returns exactly that), and trusting an absent header would miss the
 * bare `text/html`-less fallback some static hosts emit.
 *
 * @module @puffin-core/api-client/htmlResponseGuard
 */

import { HtmlResponseError } from './errors/HtmlResponseError.js';

/**
 * axios `responseType` values that mean "the caller did NOT ask for JSON".
 * `undefined` is NOT in this set — axios's default IS json.
 */
const NON_JSON_RESPONSE_TYPES = new Set(['text', 'blob', 'document', 'arraybuffer', 'stream']);

/**
 * Leading-markup sniff, tolerant of a BOM and leading whitespace.
 *
 * The BOM is written as the ESCAPE sequence, never as a literal character:
 * `no-irregular-whitespace` rejects the raw byte, and a raw BOM in source is
 * exactly the invisible-corruption class `no-control-characters` exists to stop.
 */
const HTML_BODY_HEAD_RE = /^\uFEFF?\s*<(?:!doctype\s+html|html[\s>])/i;

/**
 * Read a header case-insensitively from any of the three shapes this ecosystem
 * produces: an axios v1 `AxiosHeaders` (has `.get()`), a plain object bag (test
 * doubles + the fetch adapter's normalized shape), and a `fetch` `Headers`
 * instance (also has `.get()`).
 *
 * @param {*} headers - Header container of any supported shape.
 * @param {string} name - Header name (case-insensitive).
 * @returns {string|null} The header value, or null when absent/unreadable.
 */
export function readHeader(headers, name) {
  if (!headers) {
    return null;
  }
  if (typeof headers.get === 'function') {
    const viaGetter = headers.get(name);
    return typeof viaGetter === 'string' ? viaGetter : null;
  }
  if (typeof headers !== 'object') {
    return null;
  }
  const lowered = name.toLowerCase();
  const match = Object.keys(headers).find((key) => key.toLowerCase() === lowered);
  const value = match === undefined ? undefined : headers[match];
  return typeof value === 'string' ? value : null;
}

/**
 * Decide whether a response body is an HTML document delivered to a caller that
 * asked for JSON.
 *
 * Pure — no throwing, no axios coupling — so it is unit-testable on its own and
 * reusable from the non-axios clients.
 *
 * @param {Object} args
 * @param {string|null} [args.contentType] - Response content-type header, if any.
 * @param {*} [args.body] - The parsed/raw response body.
 * @param {string} [args.responseType] - The caller's requested response type.
 * @param {boolean} [args.allowHtmlResponse=false] - Explicit caller opt-out.
 * @returns {boolean} True when this response should be treated as a failure.
 */
export function isUnexpectedHtmlResponse({
  contentType,
  body,
  responseType,
  allowHtmlResponse = false,
} = {}) {
  if (allowHtmlResponse === true) {
    return false;
  }
  if (typeof responseType === 'string' && NON_JSON_RESPONSE_TYPES.has(responseType)) {
    return false;
  }

  if (typeof contentType === 'string' && contentType.length > 0) {
    const normalized = contentType.toLowerCase();
    // Rule 3: the header is authoritative in BOTH directions.
    if (normalized.includes('application/json') || normalized.includes('+json')) {
      return false;
    }
    return normalized.includes('text/html');
  }

  // Rule 4: no content-type at all — fall back to sniffing the body head.
  return typeof body === 'string' && HTML_BODY_HEAD_RE.test(body);
}

/**
 * The installed detection reporter. Module-scoped, defaults to a no-op.
 *
 * @type {?Function}
 */
let htmlResponseReporter = null;

/**
 * Install a reporter invoked EVERY time an unexpected HTML body is detected — AAS-I18N-002 §11.10.
 *
 * ## Why a throw is not enough
 *
 * `HtmlResponseError` tells you about the one instance you happened to hit, in the one session you
 * happened to be watching. The defect it guards against is **silent data loss that ran for months**:
 * a `basePath` missing its `/api/v1` prefix resolved to the web origin, Vite's SPA history fallback
 * answered **200 + `index.html`**, and four apps discarded every column / filter / sort write with
 * no error anywhere. `createDashboardGroupAdapter` fired **zero times per page load** for months,
 * masked by a fallback that made the UI look correct. A local throw would not have surfaced any of
 * that — nobody was looking at that console.
 *
 * A reporter turns each detection into a signal that leaves the browser, so the SECOND occurrence is
 * a datapoint rather than a repeat of the first invisible one.
 *
 * ## Contract
 *
 * The reporter is **advisory**: it runs BEFORE the throw and does not prevent or delay it. It must
 * not throw — the canonical reporter cannot (see `assertNotHtmlResponse`). A reporter that throws
 * anyway is a defect in that reporter, and its error surfaces rather than being swallowed
 * (SWALLOWED-CATCH U3: this used to discard it, so a broken reporter reported nothing, silently —
 * the very invisibility the reporter exists to end).
 *
 * @param {?Function} reporter - `({endpoint, method, status, contentType, requestUrl}) => void`,
 *   or `null` to uninstall.
 * @returns {void}
 */
export function setHtmlResponseReporter(reporter) {
  htmlResponseReporter = typeof reporter === 'function' ? reporter : null;
}

/**
 * Uninstall the reporter. Exists so a test can restore module state without reaching into internals.
 *
 * @returns {void}
 */
export function resetHtmlResponseReporter() {
  htmlResponseReporter = null;
}

/**
 * The canonical event NAME every app emits for an HTML-response detection.
 *
 * Exported so a dashboard, an alert rule and a test can all name the SAME string. An event whose
 * name is retyped per app is an event nobody can query across the fleet.
 *
 * 🔴 A NAME, not a type (MUTATION-INSTRUMENT-001 F-TOCONTAIN-3 F1, 2026-09-25). It used to be
 * exported as an "event type" and handed to the service as `eventType`; the portal service sends
 * every named event as eventType `custom`, so the stored rows were always
 * `custom / html_response_detected` while the alert rule and a contract looked for it in the TYPE
 * column. Both are re-pointed to the name (`htmlResponseEventNameAlerted.contract`).
 *
 * @constant {string}
 */
export const HTML_RESPONSE_EVENT_NAME = 'html_response_detected';

/**
 * Install the canonical reporter: forward every HTML-response detection to a portal-analytics
 * service as an `html_response_detected` event.
 *
 * ## Why this is a factory and not four lines per app
 *
 * console and pos-web wrote this wiring **identically** — same event name, same five properties.
 * AAS-I18N-002 ruling 74 extends it to the other eight web apps, which would make ten hand-written
 * copies of one event schema. Factory-first (no rule of three): the copies are collapsed here BEFORE
 * the eight are added, so a property rename is one edit and the event name cannot drift per app.
 *
 * ## Contract
 *
 * Inherits `setHtmlResponseReporter`'s: **advisory**. It runs before the throw and cannot prevent
 * it. `trackEvent` enqueues synchronously and cannot throw at runtime; a service whose `trackEvent`
 * throws is a defect, and it surfaces.
 *
 * ⚠️ Call this at MODULE scope beside the service, never inside a React effect: the defect can occur
 * on the very first request a page issues, which is before any component has mounted.
 *
 * @param {{trackEvent: Function}} analyticsService - a portal-analytics service
 * @returns {void}
 */
export function reportHtmlResponsesToAnalytics(analyticsService) {
  if (!analyticsService || typeof analyticsService.trackEvent !== 'function') {
    // A silent no-op here would be the worst outcome: the app would look wired and report nothing,
    // which is precisely the invisibility this guard exists to end.
    throw new TypeError(
      'reportHtmlResponsesToAnalytics: expected an analytics service exposing trackEvent()',
    );
  }
  setHtmlResponseReporter(({ endpoint, method, status, contentType, requestUrl }) => {
    analyticsService.trackEvent({
      eventName: HTML_RESPONSE_EVENT_NAME,
      properties: { endpoint, method, status, contentType, requestUrl },
    });
  });
}

/**
 * Assert an axios response is not an unexpected HTML document; throw
 * `HtmlResponseError` when it is.
 *
 * Returns the response unchanged on the happy path so it can be dropped
 * straight into an axios fulfilled interceptor:
 * `client.interceptors.response.use(assertNotHtmlResponse, onRejected)`.
 *
 * @param {import('axios').AxiosResponse} response - The axios response.
 * @returns {import('axios').AxiosResponse} The same response.
 * @throws {HtmlResponseError} When the body is HTML and JSON was expected.
 */
export function assertNotHtmlResponse(response) {
  // A response without a config is not something we can diagnose (and axios
  // always supplies one); pass it through rather than invent a false positive.
  const reqConfig = response && response.config;
  if (!reqConfig) {
    return response;
  }

  const contentType = readHeader(response.headers, 'content-type');
  if (
    !isUnexpectedHtmlResponse({
      contentType,
      body: response.data,
      responseType: reqConfig.responseType,
      allowHtmlResponse: reqConfig.allowHtmlResponse,
    })
  ) {
    return response;
  }

  const detail = {
    endpoint: reqConfig.url || '(unknown url)',
    method: reqConfig.method || 'get',
    status: response.status,
    contentType,
    requestUrl: `${reqConfig.baseURL || ''}${reqConfig.url || ''}`,
  };

  // §11.10: report BEFORE throwing. No catch around the reporter (SWALLOWED-CATCH U3): the one
  // production reporter (`reportHtmlResponsesToAnalytics` → portal-analytics `trackEvent` →
  // `AnalyticsService#trackEvent`: a consent check and a synchronous `EventQueue#enqueue`, whose
  // batch send is async and never awaited here) cannot throw at runtime. A reporter that throws is a
  // defect in the reporter, and it surfaces instead of vanishing.
  if (htmlResponseReporter) htmlResponseReporter(detail);

  throw new HtmlResponseError(detail);
}

export default assertNotHtmlResponse;
